import { Box } from '@/components/box/box';
import { useEffect, useState } from 'react';
import { useFromPreviousValue } from '@/hooks/use-from-previous-value';
import { urlForImage } from '@/lib/sanity/sanity';
import { TreemapImage } from '@/components/treemap-image/treemap-image';
import { WorkProject } from '@/lib/schemas/work-project';
import { SanityKeyed } from 'sanity-codegen';
import { SanityResolvedImage } from '@/types';

type ImageCarouselProps = {
  canAnimate: boolean;
  images?: SanityKeyed<SanityResolvedImage>[];
  project?: WorkProject;
};

export function ImageCarousel(props: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasIncrementedIndex, setHasIncrementedIndex] = useState(false);
  const previousIndex = useFromPreviousValue(currentIndex);
  const [isRotating, setIsRotating] = useState(false);

  const images = (() => {
    if (!props.project)
      return props.images?.map((image) => ({
        key: image._key,
        title: '',
        url: urlForImage(image.asset).url(),
        palette: image.asset.metadata.palette,
      }));

    // the list of images takes preference if it's provided
    if (props.project.images && props.project.images.length > 0) {
      return props.project.images.map((image, index) => {
        return {
          key: `${props.project?._key}-${index}`,
          title: props.project?.target.title,
          url: urlForImage(image.asset).url(),
          palette: image.asset.metadata.palette,
        };
      });
    }

    // otherwise default to using the target project
    return [
      {
        key: props.project.target._id,
        title: props.project.target.title,
        url: urlForImage(
          props.project.target.workOverviewImage
            ? props.project.target.workOverviewImage
            : props.project.target.cover
        ).url(),
        palette: props.project.target.workOverviewImage
          ? props.project.target.workOverviewImage.asset.metadata.palette
          : props.project.target.cover.asset.metadata.palette,
      },
    ];
  })();

  if (!images) {
    throw Error('No images provided');
  }

  // rotation logic
  useEffect(() => {
    if (!props.canAnimate || images.length === 1 || !isRotating) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    function scheduleRotate() {
      timeoutId = setTimeout(onRotate, 5000);
    }

    function onRotate() {
      setCurrentIndex((index) => {
        return images && index === images.length - 1 ? 0 : index + 1;
      });
      setHasIncrementedIndex(true);
      scheduleRotate();
    }

    scheduleRotate();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [images.length, props.canAnimate, isRotating, images]);

  return (
    <>
      {images.map((image, index) => {
        return (
          <Box
            key={image.key}
            css={{
              position: 'absolute',
              inset: 0,
              pointerEvents: index === currentIndex ? 'auto' : 'none',
              zIndex:
                index === currentIndex ? 2 : index === previousIndex ? 1 : 0,
            }}
          >
            <TreemapImage
              src={image.url}
              alt={image.title}
              layout="fill"
              objectFit="cover"
              palette={image.palette}
              canAnimate={props.canAnimate}
              onReady={() => {
                if (index === 0) {
                  setIsRotating(true);
                }
              }}
              animationState={(() => {
                if (index === 0 && !hasIncrementedIndex) {
                  return 'initial';
                }

                if (index === currentIndex) {
                  return 'enter';
                }

                if (index === previousIndex) {
                  return 'leave';
                }
              })()}
            />
          </Box>
        );
      })}
    </>
  );
}
